import type { QuizAnswers } from "@/features/quiz"
import { getZodiacSlug } from "@/features/user"

import type { PdfReportUserPayload } from "../api/pdf-report-service"

/**
 * Per-offer builders that turn raw quiz answers into the minimal report snapshot
 * the backend actually consumes. Each report reads a different subset (star &
 * astro-map are zodiac-driven, love-match is a static PDF), so we build a
 * dedicated payload per offer and omit every optional field the quiz didn't
 * collect instead of sending empty placeholders. This also means an offer no
 * longer depends on `buildUserProfile` (which requires a `name` the astro-map /
 * soulmate funnels never collect) — the request fires whenever the report's own
 * required fields are present.
 */

/** A quiz answer trimmed to a non-empty string, or null. */
function asString(value: QuizAnswers[string] | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null
}

/** Split a `YYYY-MM-DD` birth date into numeric parts, or null on bad input. */
function birthDateParts(dob: string): { year: number; month: number; day: number } | null {
  const [year, month, day] = dob.split("-").map(Number)
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null
  return { year, month, day }
}

/** Convert the quiz time ("9:00 AM" / "12:30 PM" / "unknown") to 24h parts, or null. */
function birthTimeParts(value: string | null): { hour: number; minute: number } | null {
  if (!value || value === "unknown") return null
  const match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null
  let hour = Number(match[1]) % 12
  if (match[3].toUpperCase() === "PM") hour += 12
  return { hour, minute: Number(match[2]) }
}

/**
 * Star report snapshot. The reading is zodiac-driven, so a valid birth date is
 * required (returns null without it — the caller skips the request rather than
 * charging for a report with a random sign). Name and birth time are
 * personalisation only and omitted when absent; gender / marital status are
 * ignored by the public endpoint and never sent.
 */
export function buildStarReportUser(answers: QuizAnswers): PdfReportUserPayload | null {
  const dob = asString(answers.dob)
  if (!dob) return null
  const zodiac = getZodiacSlug(dob)
  const parts = birthDateParts(dob)
  if (!zodiac || !parts) return null

  const user: PdfReportUserPayload = {
    zodiac,
    dayOfBirth: parts.day,
    monthOfBirth: parts.month,
    yearOfBirth: parts.year,
  }

  const name = asString(answers.name)
  if (name) user.name = name

  const time = birthTimeParts(asString(answers.tob))
  if (time) {
    user.hourOfBirth = time.hour
    user.minuteOfBirth = time.minute
    user.timeFormat = "24"
  }

  return user
}

/**
 * Love Match snapshot. The report is a static PDF, so the backend reads only the
 * optional name (email greeting) and ignores everything else. Never returns null
 * — the request needs just a subscription id + email, which the caller supplies.
 */
export function buildLoveMatchReportUser(answers: QuizAnswers): PdfReportUserPayload {
  const name = asString(answers.name)
  return name ? { name } : {}
}

/**
 * Astro Map snapshot. The backend picks one of 12 static PDFs by zodiac, so a
 * valid birth date is required (returns null without it). Name is an email
 * greeting only and omitted when absent; every other quiz field is unused and
 * never sent.
 */
export function buildAstroMapReportUser(answers: QuizAnswers): PdfReportUserPayload | null {
  const dob = asString(answers.dob)
  if (!dob) return null
  const zodiac = getZodiacSlug(dob)
  if (!zodiac) return null

  const user: PdfReportUserPayload = { zodiac }

  const name = asString(answers.name)
  if (name) user.name = name

  return user
}
